/* The second-factor step, shown by AppShell in place of the product until this
 * session has passed it (`02` -> A local account signs in with a password and
 * Arborcision's second factor, AR-290).
 *
 * The API refuses every data route until then, so this screen is what the
 * person can reach, not the control.
 */
import { useState, type FormEvent } from "react";
import { signOut } from "../auth";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Logo } from "../components/Logo";
import { Notice } from "../components/Notice";
import {
  useFinishStep,
  useSendEmailCode,
  useStartTotp,
  useVerify,
  type SecondFactorMethod,
  type StepStatus,
  type TotpEnrolment,
} from "../api/secondFactor";
import { afterVerify, firstView, type View } from "../lib/secondFactorView";
import s from "./SecondFactorStep.module.css";

const CODE_LABEL: Record<SecondFactorMethod, string> = {
  totp: "Code from your authenticator app",
  email: "Code from your email",
  recovery: "Recovery code",
};

export function SecondFactorStep({ status }: { status: StepStatus }) {
  const [view, setView] = useState<View>(() => firstView(status));
  const [code, setCode] = useState("");
  // The server has passed this sign-in, so every screen after it can continue
  // into the product rather than only finishing the authenticator setup.
  const [passed, setPassed] = useState(false);
  const startTotp = useStartTotp();
  const sendEmail = useSendEmailCode();
  const verify = useVerify();
  const finish = useFinishStep();

  const error = startTotp.error ?? sendEmail.error ?? verify.error;

  function enter(
    method: SecondFactorMethod,
    enrolment?: TotpEnrolment,
    sent?: boolean,
    addTotp?: boolean,
  ) {
    setCode("");
    verify.reset();
    setView({ kind: "enter", method, enrolment, sent, addTotp });
  }

  function chooseTotp() {
    startTotp.mutate(undefined, { onSuccess: (enrolment) => enter("totp", enrolment) });
  }

  function addAuthenticator() {
    setPassed(true);
    setView({ kind: "adding" });
    chooseTotp();
  }

  function chooseEmail(addTotp?: boolean) {
    sendEmail.mutate(undefined, { onSuccess: () => enter("email", undefined, true, addTotp) });
  }

  // An emailed code already sent is still the one to enter, so asking for the
  // authenticator then only changes what follows it.
  function askForTotp() {
    if (view.kind === "enter" && view.sent) setView({ ...view, addTotp: true });
    else chooseEmail(true);
  }

  const canAskForTotp = !status.methods.includes("totp");

  function submit(event: FormEvent, method: SecondFactorMethod) {
    event.preventDefault();
    verify.mutate(
      { method, code: code.trim() },
      {
        onSuccess: (result) => {
          const addTotp = view.kind === "enter" && !!view.addTotp;
          const next = afterVerify(addTotp, result.recovery_codes);
          if (next.kind === "codes") {
            setPassed(true);
            setView(next);
          } else if (next.kind === "addTotp") addAuthenticator();
          else void finish();
        },
      },
    );
  }

  return (
    <div className={s["step"]}>
      <div className={s["step__card"]}>
        <Logo height={22} />
        <h1 className="auth__title">Confirm your sign-in</h1>

        {error && <Notice tone="error">{error.message}</Notice>}

        {view.kind === "choose" && (
          <>
            <p className="auth__lede">Choose how to confirm each sign-in.</p>
            <Button variant="primary" size="lg" onClick={chooseTotp} disabled={startTotp.isPending}>
              Use an authenticator app
            </Button>
            <Button size="lg" onClick={() => chooseEmail()} disabled={sendEmail.isPending}>
              Email me a code
            </Button>
          </>
        )}

        {view.kind === "enter" && view.method === "email" && !view.sent && (
          <>
            <p className="auth__lede">We will email you a code to confirm this sign-in.</p>
            <Button variant="primary" size="lg" onClick={() => chooseEmail()} disabled={sendEmail.isPending}>
              Email me a code
            </Button>
            {canAskForTotp && (
              <Button variant="tertiary" onClick={askForTotp} disabled={sendEmail.isPending}>
                Set up an authenticator app
              </Button>
            )}
            {status.recovery_codes_left > 0 && (
              <Button variant="tertiary" onClick={() => enter("recovery")}>
                Use a recovery code
              </Button>
            )}
          </>
        )}

        {view.kind === "enter" && (view.method !== "email" || view.sent) && (
          <form className={s["step__form"]} onSubmit={(event) => submit(event, view.method)}>
            {view.enrolment && (
              <>
                <p className="auth__lede">
                  Scan this code with Google or Microsoft Authenticator, then enter the six-digit code
                  it shows.
                </p>
                <img
                  className={s["step__qr"]}
                  src={view.enrolment.qr_svg}
                  alt="QR code for your authenticator app"
                />
                <p className="auth__lede">
                  Or enter this key in the app: <span className="mono">{view.enrolment.secret}</span>
                </p>
              </>
            )}
            {view.method === "email" && !view.enrolment && (
              <p className="auth__lede">
                A code is on its way to your email. It works once.
                {view.addTotp && " Enter it to confirm it is you, then set up your authenticator app."}
              </p>
            )}
            <Field
              label={CODE_LABEL[view.method]}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode={view.method === "recovery" ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              required
            />
            <Button type="submit" variant="primary" disabled={verify.isPending || !code.trim()}>
              Confirm
            </Button>
            {passed ? (
              <div className={s["step__alternatives"]}>
                <Button variant="tertiary" onClick={() => void finish()}>
                  Continue without the app
                </Button>
              </div>
            ) : (
              <div className={s["step__alternatives"]}>
                {view.method !== "email" && (
                  <Button variant="tertiary" onClick={() => chooseEmail()} disabled={sendEmail.isPending}>
                    Email me a code
                  </Button>
                )}
                {view.method === "email" && (
                  <Button
                    variant="tertiary"
                    onClick={() => chooseEmail(view.addTotp)}
                    disabled={sendEmail.isPending}
                  >
                    Send another code
                  </Button>
                )}
                {view.method === "email" && canAskForTotp && !view.addTotp && (
                  <Button
                    variant="tertiary"
                    onClick={askForTotp}
                    disabled={verify.isPending || sendEmail.isPending}
                  >
                    Set up an authenticator app
                  </Button>
                )}
                {view.method !== "recovery" && status.recovery_codes_left > 0 && (
                  <Button variant="tertiary" onClick={() => enter("recovery")}>
                    Use a recovery code
                  </Button>
                )}
                {status.methods.length === 0 && (
                  <Button variant="tertiary" onClick={() => setView({ kind: "choose" })}>
                    Choose another method
                  </Button>
                )}
              </div>
            )}
          </form>
        )}

        {view.kind === "codes" && (
          <>
            <p className="auth__lede">
              Save these recovery codes. Each one confirms a sign-in once if you lose your
              authenticator or your email. They are shown only now.
            </p>
            <ol className={`mono ${s["step__codes"]}`}>
              {view.codes.map((recovery) => (
                <li key={recovery}>{recovery}</li>
              ))}
            </ol>
            <Button
              variant="primary"
              onClick={view.addTotp ? addAuthenticator : () => void finish()}
            >
              I have saved these codes
            </Button>
          </>
        )}

        {view.kind === "adding" && (
          <>
            <p className="auth__lede">
              This sign-in is confirmed. Next, set up your authenticator app.
            </p>
            {startTotp.isError && (
              <Button variant="primary" onClick={chooseTotp}>
                Try again
              </Button>
            )}
            <Button variant="tertiary" onClick={() => void finish()}>
              Continue without the app
            </Button>
          </>
        )}

        <Button variant="tertiary" className={s["step__sign-out"]} onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
